import { CommonText, CommonTitle } from '@client/style/Text';
import { Colors } from '@client/style/Variables';
import React, { FormEvent, useCallback, useState } from 'react';
import styled from 'styled-components';
import { PillButton } from '@client/modules/shared/Buttons';
import { TextStyle } from '@client/style/TextStyles';
import RegistrationStore from '@client/modules/MyPage/RegistrationStore';
import { RegistrationInput } from './RegistrationInput';

/**
 * Text
 */
const Content = styled.form`
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 20px;
`;

const StyledCommonTitle = styled(CommonTitle)`
	color: ${Colors.darkText};
	${TextStyle.size('p3', 'p1', 'laptop')}
`;

const StyledCommonText = styled(CommonText)`
	color: ${Colors.darkText};
`;

const ResponseText = styled(CommonText)<{ $success: boolean }>`
	color: ${props => (props.$success ? Colors.darkText : Colors.error)};
`;

const ButtonsContainer = styled.div`
	padding-bottom: 20px;
	display: flex;
	gap: 10px;
	width: 100%;
	justify-content: center;
`;

interface IProps {
	onSuccess?: () => void;
	onError?: () => void;
}

export const RegistrationForm = ({ onSuccess, onError }: IProps) => {
	const token = RegistrationStore.use(state => state.token);

	const [isLoading, setIsLoading] = useState(false);
	const [isDeleting, setIsDeleting] = useState(false);

	const [response, setResponse] = useState<{ content: string; success: boolean }>();

	const [input, setInput] = useState({
		value: {
			serial: '',
			control: '',
		},
		valid: false,
	});

	const onRegisterSubmit = useCallback(
		(ev: FormEvent) => {
			ev.preventDefault();
			if (!input.valid || isLoading || isDeleting) return;
			setIsLoading(true);
			RegistrationStore.setToken(input.value)
				.then(() => {
					setResponse({
						content: 'Sådan! Det er forbundet!',
						success: true,
					});
					onSuccess?.();
				})
				.catch((err: Error) => {
					if (err.message === 'Invalid token registered') {
						setResponse({
							content: 'Hovsa! Der skete en fejl... Har du tastet koden korrekt?',
							success: false,
						});
					} else {
						setResponse({
							content: 'Hovsa! Der skete en fejl...',
							success: false,
						});
					}
					console.warn(err);
					onError?.();
				})
				.finally(() => {
					setIsLoading(false);
				});
		},
		[input, isLoading, isDeleting, setIsLoading]
	);

	const onRemoveClick = useCallback(() => {
		if (isLoading || isDeleting) return;
		setIsDeleting(true);
		RegistrationStore.removeToken()
			.then(() => {
				setResponse({
					content: 'Tjek! Forbindelsen er fjernet',
					success: true,
				});
				onSuccess?.();
			})
			.catch(err => {
				setResponse({
					content: 'Hovsa! Der skete en fejl...',
					success: false,
				});
				console.warn(err);
				onError?.();
			})
			.finally(() => {
				setIsDeleting(false);
			});
	}, [isLoading, isDeleting, setIsDeleting, setResponse]);

	return (
		<Content onSubmit={onRegisterSubmit}>
			<StyledCommonTitle>Forbind armbånd</StyledCommonTitle>
			<StyledCommonText>Indtast de to unikke koder fra dit armbånd for at forbinde det til din konto</StyledCommonText>

			<RegistrationInput initialValue={token} disabled={isLoading || isDeleting} onChange={setInput} />

			{response && <ResponseText $success={response.success}>{response.content}</ResponseText>}

			{(!response || !response.success) && (
				<ButtonsContainer>
					<PillButton
						disabled={!input.valid || isLoading || isDeleting}
						type='submit'
						colors={{
							bg: Colors.darkBackground,
							fg: Colors.lightText,
						}}
					>
						{isLoading ? 'Arbejder...' : 'Forbind nu'}
					</PillButton>
					{token ? (
						<PillButton
							onClick={onRemoveClick}
							disabled={isLoading || isDeleting}
							colors={{
								bg: Colors.darkBackground,
								fg: Colors.lightText,
							}}
						>
							{isDeleting ? 'Fjerner...' : 'Fjern forbindelse'}
						</PillButton>
					) : (
						<></>
					)}
				</ButtonsContainer>
			)}
		</Content>
	);
};
